// Group picker — header tab bar (TabBar component handles desktop strip
// vs mobile dropdown) plus the "More agents" non-member browser modal.
import { useEffect } from 'preact/hooks';
import type { JSX } from 'preact';

import {
  groups,
  groupId,
  isElevatedUser,
  nowTick,
  groupPickerOpen,
  groupPickerMode,
  credentialsOpen,
} from '../state';
import { openChat } from '../credentials-navigation';
import { navigateAttention, selectGroup } from '../actions';
import { fmtRelative, fmtAbsolute } from '../utils';
import type { Group } from '../types';
import { createGroupOpen } from './CreateGroupModal';
import { TabBar, type TabItem, type TabExtra } from './TabBar';
import { MobileDialog, MobileDialogItem, MobileDialogList } from './MobileDialog';
import { AttentionBadge } from './AttentionBadge';
import { attentionError, groupAttentionCount } from '../attention-state';

function attentionBadge(group: Group) {
  const count = groupAttentionCount(group.id);
  const label = attentionError.value
    ? `Input status unavailable. ${count} last known requests for ${group.name}`
    : `${count} requests need your input. Open oldest request in ${group.name}`;
  return count > 0 ? <AttentionBadge value={count} label={label} /> : undefined;
}

function isElevatedOnly(g: Group): boolean {
  return g.elevatedOnly === true;
}

interface ChipParts {
  parts: string[];
  isAdminOnly: boolean;
}

function chipParts(g: Group, elevated: boolean): ChipParts {
  const isAdminOnly = elevated && isElevatedOnly(g);
  const parts: string[] = [];
  if (!g.isAdmin) parts.push('\uD83D\uDD12');
  if (isAdminOnly) parts.push('\uD83D\uDC41');
  if (g.lastActivityAt) parts.push(fmtRelative(g.lastActivityAt));
  return { parts, isAdminOnly };
}

function tipFor(g: Group, isAdminOnly: boolean): string {
  const parts: string[] = [g.name];
  if (isAdminOnly) parts.push('Visible to you as admin');
  if (g.lastActivityAt) parts.push(fmtAbsolute(g.lastActivityAt));
  return parts.join(' \u00B7 ');
}

export function GroupStrip(): JSX.Element | null {
  const elevated = isElevatedUser.value;
  // Subscribe so relative times refresh.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  nowTick.value;
  const all = groups.value;
  // Groups requiring system-wide privileges belong exclusively in the eye browser.
  const memberGroups = all.filter((g) => !isElevatedOnly(g));
  const active = all.find((g) => g.id === groupId.value);

  const items: TabItem[] = memberGroups.map((g) => {
    const { parts, isAdminOnly } = chipParts(g, elevated);
    const sublabel = parts.join(' \u00B7 ');
    return {
      id: g.id,
      label: g.name,
      badge: attentionBadge(g),
      badgeDisabled: !!attentionError.value,
      onBadgeClick: () => { void navigateAttention(g.id); },
      sublabel: sublabel || undefined,
      reserveSublabel: true,
      title: tipFor(g, isAdminOnly),
      className: isAdminOnly ? 'is-admin-visible' : undefined,
    };
  });

  const extras: TabExtra[] = [];
  if (elevated) {
    extras.push({
      key: 'new',
      label: 'New Agent\u2026',
      reserveSublabel: true,
      title: 'Create a new agent group',
      ariaHaspopup: 'dialog',
      onClick: () => { createGroupOpen.value = true; },
      className: 'group-extra-new',
    });
  }

  const pick = (gid: string): void => {
    if (credentialsOpen.value) openChat();
    if (gid !== groupId.value) selectGroup(gid).catch(console.error);
  };

  return (
    <TabBar
      ariaLabel="Agent groups"
      mobileSheetTitle="Agent groups"
      mobileFallbackLabel={active?.name}
      activeId={active?.id ?? null}
      items={items}
      onSelect={pick}
      extras={extras.length ? extras : undefined}
      className="group-strip tab-bar-header"
    />
  );
}

export function GroupPickerModal() {
  const open = groupPickerOpen.value;
  const mode = groupPickerMode.value;
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  nowTick.value;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') groupPickerOpen.value = false;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const elevated = isElevatedUser.value;
  const all = groups.value;
  const visible = (() => {
    if (mode === 'non-members') return all.filter(isElevatedOnly);
    return elevated ? all : all.filter((g) => !isElevatedOnly(g));
  })();
  const title = mode === 'non-members' ? 'More agents' : 'Agent groups';

  const close = (): void => { groupPickerOpen.value = false; };
  const pick = (gid: string): void => {
    close();
    if (gid !== groupId.value) selectGroup(gid).catch(console.error);
  };

  return (
    <MobileDialog title={title} onClose={close} maxWidth="480px">
      <MobileDialogList>
        {visible.map((g) => {
          const isActive = g.id === groupId.value;
          const { parts, isAdminOnly } = chipParts(g, elevated);
          const subtitle = parts.join(' \u00B7 ');
          return (
            <MobileDialogItem
              key={g.id}
              label={g.name}
              badge={attentionBadge(g)}
              badgeDisabled={!!attentionError.value}
              onBadgeClick={() => { close(); void navigateAttention(g.id); }}
              sublabel={subtitle || undefined}
              active={isActive}
              className={isAdminOnly ? 'is-admin-visible' : undefined}
              title={tipFor(g, isAdminOnly)}
              onClick={() => pick(g.id)}
            />
          );
        })}
      </MobileDialogList>
    </MobileDialog>
  );
}
